import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { baseUrl } from "../../baseUrl";
import { FaLock, FaEye, FaEyeSlash, FaSignOutAlt } from "react-icons/fa";

export const TpinLockModal = ({ open, onUnlock, onLogout }) => {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Prevent background scrolling completely while screen is locked
  useEffect(() => {
    if (open) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      // Focus the input when locked
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 150);

      // Prevent mobile/desktop scroll events while modal is active
      const preventTouchMove = (e) => {
        if (
          e.target &&
          (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON")
        ) {
          return;
        }
        e.preventDefault();
      };

      window.addEventListener("touchmove", preventTouchMove, {
        passive: false,
      });

      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
        window.removeEventListener("touchmove", preventTouchMove);
        clearTimeout(timer);
      };
    }
  }, [open]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setPin("");
      setError("");
      setLoading(false);
      setShowPin(false);
    }
  }, [open]);

  const handlePinChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 4);
    setPin(val);
    if (error) setError("");
  };

  const handleUnlock = async (e) => {
    if (e) e.preventDefault();
    if (pin.length !== 4) {
      setError("Please enter your 4-digit TPIN");
      return;
    }

    const token =
      localStorage.getItem("userToken") ||
      localStorage.getItem("userIdToken");

    if (!token) {
      setError("Session expired. Please log in again.");
      setTimeout(() => {
        onLogout();
      }, 1200);
      return;
    }

    setLoading(true);
    setError("");

    try {
      let latitude = "28.6139";
      let longitude = "77.2090";

      // Attempt fast geolocation if permitted, else use safe fallback coordinates
      if (typeof window !== "undefined" && navigator?.geolocation) {
        try {
          const pos = await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (p) => resolve(p),
              () => resolve(null),
              { timeout: 1500, enableHighAccuracy: false, maximumAge: 300000 }
            );
          });
          if (pos?.coords?.latitude && pos?.coords?.longitude) {
            latitude = String(pos.coords.latitude);
            longitude = String(pos.coords.longitude);
          }
        } catch (e) {
          // fallback coordinates retained
        }
      }

      const res = await axios.post(
        `${baseUrl}user/verify-mobile-location-pin`,
        {
          pin,
          latitude,
          longitude,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res?.data?.statusCode === 200 || res?.data?.error === false) {
        setPin("");
        setError("");
        onUnlock();
      } else {
        setError(res?.data?.message || "Invalid TPIN. Please try again.");
      }
    } catch (err) {
      console.error("TPIN verification error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Invalid TPIN. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999999,
        background:
          "radial-gradient(circle at center, rgba(16, 32, 60, 0.96) 0%, rgba(8, 16, 30, 0.98) 100%)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        touchAction: "none",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: "400px",
          width: "100%",
          background:
            "linear-gradient(165deg, rgba(26, 46, 80, 0.95) 0%, rgba(13, 25, 46, 0.98) 100%)",
          border: "1px solid rgba(204, 152, 64, 0.35)",
          borderRadius: "20px",
          boxShadow:
            "0 30px 60px rgba(0, 0, 0, 0.7), 0 0 50px rgba(204, 152, 64, 0.15)",
          padding: "32px 24px",
          textAlign: "center",
          color: "#fff",
          position: "relative",
          animation: "fadeInScale 0.25s ease-out",
        }}
      >
        {/* Lock Icon Badge */}
        <div
          style={{
            width: "68px",
            height: "68px",
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(204, 152, 64, 0.2) 0%, rgba(204, 152, 64, 0.05) 100%)",
            border: "1.5px solid rgba(204, 152, 64, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px auto",
            boxShadow: "0 0 25px rgba(204, 152, 64, 0.2)",
          }}
        >
          <FaLock style={{ color: "#e7b96d", fontSize: "28px" }} />
        </div>

        {/* Security Tag */}
        <div
          style={{
            display: "inline-block",
            padding: "3px 12px",
            borderRadius: "20px",
            background: "rgba(204, 152, 64, 0.15)",
            border: "1px solid rgba(204, 152, 64, 0.3)",
            color: "#e7b96d",
            fontSize: "12px",
            fontWeight: "600",
            letterSpacing: "1px",
            textTransform: "uppercase",
            marginBottom: "10px",
          }}
        >
          BankMudraa Security
        </div>

        {/* Title */}
        <h4
          style={{
            color: "#ffffff",
            fontWeight: "700",
            fontSize: "22px",
            marginBottom: "8px",
            letterSpacing: "-0.3px",
          }}
        >
          Screen Locked
        </h4>

        {/* Subtitle */}
        <p
          style={{
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: "13px",
            lineHeight: "1.5",
            marginBottom: "24px",
          }}
        >
          Your session was locked due to 5 minutes of inactivity. Enter your
          4-digit TPIN to continue your work.
        </p>

        {/* Unlock Form */}
        <form onSubmit={handleUnlock}>
          <style>{`
            #tpinLockModalInput,
            input#tpinLockModalInput,
            #tpinLockModalInput:hover,
            #tpinLockModalInput:focus,
            #tpinLockModalInput:active,
            input#tpinLockModalInput[type="text"],
            input#tpinLockModalInput[type="text"]:focus,
            input#tpinLockModalInput[type="text"]:active,
            input#tpinLockModalInput[type="password"],
            input#tpinLockModalInput[type="password"]:focus,
            input#tpinLockModalInput[type="password"]:active {
              background-color: #0b1528 !important;
              background: #0b1528 !important;
              color: #ffd077 !important;
              -webkit-text-fill-color: #ffd077 !important;
              caret-color: #ffd077 !important;
              font-size: 26px !important;
              font-weight: 700 !important;
              letter-spacing: 14px !important;
              text-align: center !important;
              border: 1.5px solid rgba(204, 152, 64, 0.5) !important;
              box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4) !important;
              outline: none !important;
            }
            #tpinLockModalInput.has-error,
            #tpinLockModalInput.has-error:focus {
              border-color: #ef4444 !important;
              box-shadow: 0 0 12px rgba(239, 68, 68, 0.3) !important;
            }
            #tpinLockModalInput::placeholder {
              color: rgba(204, 152, 64, 0.3) !important;
              -webkit-text-fill-color: rgba(204, 152, 64, 0.3) !important;
            }
            #tpinLockModalInput:-webkit-autofill,
            #tpinLockModalInput:-webkit-autofill:hover,
            #tpinLockModalInput:-webkit-autofill:focus {
              -webkit-box-shadow: 0 0 0 1000px #0b1528 inset !important;
              -webkit-text-fill-color: #ffd077 !important;
            }
          `}</style>
          <div style={{ position: "relative", marginBottom: "18px" }}>
            <input
              id="tpinLockModalInput"
              ref={inputRef}
              type={showPin ? "text" : "password"}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pin}
              onChange={handlePinChange}
              placeholder="••••"
              autoComplete="off"
              disabled={loading}
              className={`tpin-lock-modal-input ${error ? "has-error" : ""}`}
              style={{
                width: "100%",
                height: "56px",
                borderRadius: "12px",
                padding: "0 45px",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
            />

            {/* Eye toggle button */}
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              tabIndex={-1}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                color: showPin ? "#ffd077" : "rgba(255, 255, 255, 0.65)",
                cursor: "pointer",
                padding: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "18px",
                zIndex: 10,
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#ffd077";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = showPin
                  ? "#ffd077"
                  : "rgba(255, 255, 255, 0.65)";
              }}
            >
              {showPin ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                borderRadius: "8px",
                color: "#fca5a5",
                fontSize: "12.5px",
                padding: "8px 12px",
                marginBottom: "18px",
                fontWeight: "500",
              }}
            >
              {error}
            </div>
          )}

          {/* Unlock Button */}
          <button
            type="submit"
            disabled={pin.length !== 4 || loading}
            style={{
              width: "100%",
              height: "48px",
              background:
                pin.length === 4 && !loading
                  ? "linear-gradient(135deg, #e7b96d 0%, #cc9840 100%)"
                  : "rgba(204, 152, 64, 0.3)",
              border: "none",
              borderRadius: "10px",
              color: pin.length === 4 && !loading ? "#0a1122" : "rgba(255, 255, 255, 0.4)",
              fontWeight: "700",
              fontSize: "15px",
              cursor: pin.length === 4 && !loading ? "pointer" : "not-allowed",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxShadow:
                pin.length === 4 && !loading
                  ? "0 4px 15px rgba(204, 152, 64, 0.35)"
                  : "none",
            }}
          >
            {loading ? (
              <>
                <div
                  className="spinner-border spinner-border-sm"
                  role="status"
                  style={{ width: "16px", height: "16px", color: "#0a1122" }}
                ></div>
                <span>Verifying TPIN...</span>
              </>
            ) : (
              <span>Unlock Screen</span>
            )}
          </button>
        </form>

        {/* Logout Option */}
        <div style={{ marginTop: "14px" }}>
          <button
            type="button"
            onClick={onLogout}
            style={{
              width: "100%",
              height: "42px",
              background: "rgba(239, 68, 68, 0.08)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
              borderRadius: "10px",
              color: "#f87171",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.18)";
              e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.45)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
              e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.25)";
            }}
          >
            <FaSignOutAlt style={{ fontSize: "14px" }} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TpinLockModal;
